"use client";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Button } from "../ui/button";
import { BRAND, MEDIA } from "../../lib/brand";

const links = [
  { label: "About", href: "#about" },
  { label: "Word Grid", href: "#featured" },
  { label: "Play", href: "#play" },
  { label: "Services", href: "#services" },
  { label: "Why Us", href: "#why" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-black/70 border-b border-white/10"
          : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        {/* Logo */}
        <a
          href={BRAND.axionTelegram}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-3 group"
          data-testid="navbar-brand"
        >
          <div className="relative h-9 w-9 rounded-xl overflow-hidden border border-white/10 glow-soft">
            <img
              src={MEDIA.axion_logo}
              alt="Axion Bots"
              className="h-full w-full object-cover"
            />
          </div>
          <span className="font-display text-lg md:text-xl font-medium tracking-tight">
            {BRAND.name}
          </span>
        </a>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm text-neutral-400 hover:text-white transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:block">
          <Button
            asChild
            className="bg-white text-black hover:bg-neutral-200 rounded-full px-5 h-10 font-medium"
            data-testid="navbar-cta"
          >
            <a href="#contact">Contact Owner</a>
          </Button>
        </div>

        {/* Mobile toggle */}
        <button
          onClick={() => setOpen(!open)}
          className="md:hidden h-10 w-10 grid place-items-center rounded-lg border border-white/10"
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden border-t border-white/10 bg-black/90 backdrop-blur-xl"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-sm text-neutral-300 py-2"
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </a>
            ))}
            <Button
              asChild
              className="bg-white text-black hover:bg-neutral-200 rounded-full mt-2"
              data-testid="mobile-navbar-cta"
            >
              <a href="#contact" onClick={() => setOpen(false)}>
                Contact Owner
              </a>
            </Button>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
}
